import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { compact, filter, map, size, slice } from 'lodash';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Loader from 'components/Loader';
import { getPendingSlidesLocation } from 'components/StudyDashboard/ProceduresPage/pendingSlidesNavigation';
import { canQueryPendingSlides } from 'components/StudyDashboard/ProceduresPage/pendingSlidesPermissions';
import { JobType } from 'interfaces/job';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { getLinkToJobsPage } from 'utils/useNavigationToJobsPage';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { usePermissions } from 'utils/usePermissions';
import { NEW_UPLOAD_CLOCK_SKEW_TOLERANCE_MS, NewIngestedSlide, SlideTileState } from './newUploadsModel';
import { getUploadsDashboardLocation } from './uploadsDashboardLocation';
import UploadStepInfoCard from './UploadStepInfoCard';
import { NEW_UPLOADS_REFRESH_MS, useNewUploadsCheck } from './useNewUploadsCheck';

const LISTED_ITEM_LIMIT = 5;
const LISTED_SLIDE_LIMIT = 10;
// Keeps the Jobs page link short; beyond this the link filters by job type only.
const MAX_JOB_IDS_IN_LINK = 50;

const tileStateLabels: Record<SlideTileState, { label: string; color: 'success' | 'info' | 'error' | 'default' }> = {
  ready: { label: 'Tiles ready', color: 'success' },
  generating: { label: 'Generating tiles', color: 'info' },
  failed: { label: 'Tile generation failed', color: 'error' },
  notStarted: { label: 'Tiles not generated yet', color: 'default' },
  unknown: { label: 'Tile status unavailable', color: 'default' },
};

interface CheckForNewUploadsProps {
  studyId: string;
  openedAt: number;
  onBack: () => void;
}

const formatTime = (timestamp: number) =>
  new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const CheckForNewUploads = ({ studyId, openedAt, onBack }: CheckForNewUploadsProps) => {
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const { getLinkToSlidePage } = useNavigationToViewerPage();
  const [showAllSlides, setShowAllSlides] = useState(false);
  const check = useNewUploadsCheck({ studyId, openedAt });
  const { newUploads, newIngestJobs, newSlides, tileStateBySlideId, slidesWithActiveImageAnalysis } = check;

  const isLoading = check.uploadsQuery.isLoading || check.ingestJobsQuery.isInitialLoading || check.isLoadingSlideJobs;
  const canOrganizeSlides = canQueryPendingSlides({ hasPermission, studyId });
  const slidesNeedingCase = filter(newSlides, (slide) => size(slide.cases) === 0);
  const slidesGeneratingTiles = filter(newSlides, (slide) => tileStateBySlideId[slide.slideId] === 'generating');
  const slidesWithImageAnalysis = filter(newSlides, (slide) => slidesWithActiveImageAnalysis.has(slide.slideId));
  const hasActiveSlideJobs = size(slidesGeneratingTiles) > 0 || size(slidesWithImageAnalysis) > 0;
  const listedSlides = showAllSlides ? newSlides : slice(newSlides, 0, LISTED_SLIDE_LIMIT);

  const ingestJobIds = compact(map(newIngestJobs, (job) => job.id));
  const ingestJobsLink = getLinkToJobsPage({
    labId,
    jobsFilters: {
      studyId,
      types: [JobType.Ingest, JobType.BatchIngest],
      ...(size(ingestJobIds) <= MAX_JOB_IDS_IN_LINK ? { jobIds: ingestJobIds } : {}),
    },
  });

  const getSlideLink = (slide: NewIngestedSlide) =>
    getLinkToSlidePage({
      slideId: slide.slideId,
      caseId: slide.cases[0]?.id,
      caseStudyId: studyId,
      labId,
    });

  const refreshButton = (
    <Button variant="outlined" size="small" disabled={check.isFetching} onClick={check.refetch}>
      {check.isFetching ? 'Checking…' : 'Check again'}
    </Button>
  );

  return (
    <Box data-testid="check-for-new-uploads" sx={{ px: 2, py: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
            Back to upload steps
          </Button>
          {refreshButton}
        </Stack>
        <Box>
          <Typography variant="subtitle2">New activity since you opened Add slides</Typography>
          <Typography variant="caption" color="text.secondary">
            Add slides was opened at {formatTime(openedAt)}. Activity from up to{' '}
            {NEW_UPLOAD_CLOCK_SKEW_TOLERANCE_MS / 60000} minutes earlier is included to allow for clock differences.
          </Typography>
        </Box>
        <Alert severity="info">
          This view refreshes automatically every {NEW_UPLOADS_REFRESH_MS / 1000} seconds. You do not need to keep it
          open. Close it whenever you like and follow progress in the app: uploads in Uploaded Files, ingest jobs on the
          Jobs tab, and new slides in Needs case assignment once their ingest job completes.
        </Alert>

        {isLoading ? (
          <Loader inline message="Checking for new uploads, ingest jobs, and slides…" />
        ) : check.uploadsQuery.isError ? (
          <Alert severity="error">Uploads could not be loaded. Use Check again to retry.</Alert>
        ) : (
          <>
            <UploadStepInfoCard
              icon={<UploadFileOutlinedIcon color="info" fontSize="small" />}
              tone="upload"
              title="New uploads"
              description={
                size(newUploads)
                  ? `${size(newUploads)} new ${pluralize('upload', size(newUploads))} found.`
                  : 'No new uploads found yet. Large uploads can take a few minutes to appear after the command finishes.'
              }
            >
              {size(newUploads) ? (
                <>
                  <NameList names={map(newUploads, (row) => row.fileName || row.name)} />
                  <Button
                    size="small"
                    variant="text"
                    component={RouterLink}
                    to={getUploadsDashboardLocation({ labId, studyId })}
                  >
                    View uploads
                  </Button>
                </>
              ) : null}
            </UploadStepInfoCard>

            <UploadStepInfoCard
              icon={<ManageSearchOutlinedIcon color="warning" fontSize="small" />}
              tone="jobs"
              title="New ingest jobs"
              description={
                !check.canViewJobs
                  ? 'Ingest job status is not available for your account.'
                  : check.ingestJobsQuery.isError
                  ? 'Ingest jobs could not be loaded. Use Check again to retry.'
                  : size(newIngestJobs)
                  ? `${check.isIngestJobListTruncated ? 'At least ' : ''}${size(newIngestJobs)} new ingest ${pluralize(
                      'job',
                      size(newIngestJobs)
                    )} started.`
                  : 'No new ingest jobs have started yet. Ingest starts after an upload is discovered.'
              }
            >
              {size(newIngestJobs) ? (
                <>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {check.ingestJobStatusCounts.inProgress ? (
                      <Chip size="small" color="info" label={`${check.ingestJobStatusCounts.inProgress} in progress`} />
                    ) : null}
                    {check.ingestJobStatusCounts.completed ? (
                      <Chip size="small" color="success" label={`${check.ingestJobStatusCounts.completed} completed`} />
                    ) : null}
                    {check.ingestJobStatusCounts.failed ? (
                      <Chip size="small" color="error" label={`${check.ingestJobStatusCounts.failed} failed`} />
                    ) : null}
                    {check.ingestJobStatusCounts.other ? (
                      <Chip size="small" label={`${check.ingestJobStatusCounts.other} other status`} />
                    ) : null}
                  </Stack>
                  <Button size="small" variant="text" component={RouterLink} to={ingestJobsLink}>
                    View ingest jobs
                  </Button>
                </>
              ) : null}
            </UploadStepInfoCard>

            <UploadStepInfoCard
              icon={<PhotoLibraryOutlinedIcon color="success" fontSize="small" />}
              tone="workflow"
              title="New slides"
              description={
                size(newSlides)
                  ? `${size(newSlides)} ${pluralize('slide', size(newSlides))} ingested into this study.`
                  : check.ingestJobStatusCounts.inProgress
                  ? `${check.ingestJobStatusCounts.inProgress} ingest ${pluralize(
                      'job',
                      check.ingestJobStatusCounts.inProgress
                    )} still running. New slides appear here once their ingest job completes.`
                  : 'No new slides yet. Slides appear here once their ingest job completes.'
              }
            >
              {check.isSlideJobsError ? (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  Tile generation and initial image analysis status could not be loaded.
                </Alert>
              ) : null}
              {hasActiveSlideJobs ? (
                <Alert severity="info" sx={{ width: '100%' }}>
                  Other jobs are still running for these slides:{' '}
                  {compact([
                    size(slidesWithImageAnalysis)
                      ? `initial image analysis (${size(slidesWithImageAnalysis)} ${pluralize(
                          'slide',
                          size(slidesWithImageAnalysis)
                        )})`
                      : null,
                    size(slidesGeneratingTiles)
                      ? `tile generation (${size(slidesGeneratingTiles)} ${pluralize(
                          'slide',
                          size(slidesGeneratingTiles)
                        )})`
                      : null,
                  ]).join(' and ')}
                  .{canOrganizeSlides && size(slidesNeedingCase) ? ' You can organize the slides into cases now.' : ''}
                </Alert>
              ) : null}
              {size(newSlides) ? (
                <Stack
                  component="ul"
                  aria-label="New slides"
                  spacing={0.75}
                  sx={{ width: '100%', m: 0, p: 0, listStyle: 'none' }}
                >
                  {map(listedSlides, (slide) => {
                    const tileState = tileStateBySlideId[slide.slideId];
                    return (
                      <Stack
                        key={slide.key}
                        component="li"
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap title={slide.name}>
                            {slide.name}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={size(slide.cases) ? 'success' : 'warning'}
                              label={
                                size(slide.cases)
                                  ? `Assigned to ${map(slide.cases, (caseSummary) => caseSummary.displayName).join(
                                      ', '
                                    )}`
                                  : 'Needs case assignment'
                              }
                            />
                            {check.canViewJobs && tileState ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={tileStateLabels[tileState].color}
                                label={tileStateLabels[tileState].label}
                              />
                            ) : null}
                            {slidesWithActiveImageAnalysis.has(slide.slideId) ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="info"
                                label="Initial image analysis running"
                              />
                            ) : null}
                          </Stack>
                        </Stack>
                        {tileState === 'ready' ? (
                          <Button size="small" variant="text" component={RouterLink} to={getSlideLink(slide)}>
                            View slide
                          </Button>
                        ) : null}
                      </Stack>
                    );
                  })}
                </Stack>
              ) : null}
              {size(newSlides) > LISTED_SLIDE_LIMIT ? (
                <Button size="small" variant="text" onClick={() => setShowAllSlides(!showAllSlides)}>
                  {showAllSlides ? 'Show fewer slides' : `Show all ${size(newSlides)} slides`}
                </Button>
              ) : null}
              {canOrganizeSlides && size(slidesNeedingCase) ? (
                <Button
                  disableElevation
                  variant="contained"
                  startIcon={<AssignmentOutlinedIcon />}
                  component={RouterLink}
                  to={getPendingSlidesLocation({ labId, studyId })}
                >
                  Assign slides to cases
                </Button>
              ) : null}
            </UploadStepInfoCard>
          </>
        )}
      </Stack>
    </Box>
  );
};

const NameList = ({ names }: { names: string[] }) => (
  <Box>
    {map(slice(names, 0, LISTED_ITEM_LIMIT), (name, index) => (
      <Typography key={`${name}-${index}`} variant="body2" noWrap title={name}>
        {name}
      </Typography>
    ))}
    {size(names) > LISTED_ITEM_LIMIT ? (
      <Typography variant="caption" color="text.secondary">
        and {size(names) - LISTED_ITEM_LIMIT} more
      </Typography>
    ) : null}
  </Box>
);

export default CheckForNewUploads;
